import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import HubCountryLinks from "@/components/HubCountryLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /police-clearance — Hub (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import { POLICE_CLEARANCE_TYPES } from "@/data/police-clearance-types";
import { TARGET_COUNTRIES } from "@/data/police-target-countries";
import { policeHubSectionsTh } from "@/data/police-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";
import { ShieldCheck, Globe2, MessageCircle, Phone, MapPin } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("police", "th", "https://nyclanguageinstitute.com/police-clearance") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/police-clearance`;
    const title = "ตรวจประวัติอาชญากรรม Police Clearance ทุกประเทศ — PCSC, FBI, ACRO, AFP, RCMP | NYC";
    const desc =
      "บริการขอตรวจประวัติอาชญากรรม (Police Clearance / Criminal Record Check) PCSC ไทย · FBI · ACRO · AFP · RCMP — สอบถามฟรี ไม่มีข้อผูกมัด ทักแชทได้ทันที";
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance", "th") },
        { name: "description", content: ctaDesc(desc, "/police-clearance", "th")},
        { name: "keywords", content: "ตรวจประวัติอาชญากรรม, Police Clearance, PCSC, FBI Background Check, ACRO, AFP, RCMP, Criminal Record Check, ใบรับรองความประพฤติ, NYC" },
        { property: "og:title", content: ctaTitle(title, "/police-clearance", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks("/police-clearance", "/en/police-clearance"),
      ],
      scripts: [
        howToSchema({
          name: "วิธีขอตรวจประวัติอาชญากรรม Police Clearance",
          description: "ขั้นตอนการขอใบรับรองความประพฤติ/ตรวจประวัติอาชญากรรม โดย NYC",
          inLanguage: "th-TH",
          totalTime: "P14D",
          estimatedCost: { currency: "THB", value: "2000" },
          steps: [
            { name: "เลือกประเภทใบตรวจประวัติ", text: "เลือกประเภทตามประเทศปลายทาง: PCSC ไทย, FBI USA, ACRO UK, AFP Australia, RCMP Canada เป็นต้น" },
            { name: "เตรียมเอกสารและพิมพ์ลายนิ้วมือ", text: "พิมพ์ลายนิ้วมือที่สำนักงาน NYC หรือสถานีตำรวจ พร้อมเตรียมพาสปอร์ตและสำเนาวีซ่า" },
            { name: "ยื่นคำขอที่หน่วยงานตำรวจ", text: "NYC ดำเนินการยื่นแทนที่ PCSC (ไทย) หรือส่งไปยังหน่วยงานต่างประเทศ FBI/ACRO/AFP/RCMP" },
            { name: "รับใบตรวจประวัติ", text: "ระยะเวลา 7-30 วันทำการ ขึ้นอยู่กับหน่วยงาน Express สำหรับ ACRO/AFP มี" },
            { name: "แปล รับรอง MFA และสถานทูต (ถ้าจำเป็น)", text: "หากใช้ในต่างประเทศ NYC จัดการแปล รับรองโดยทนาย Notary และผ่าน MFA/Apostille ครบจบในที่เดียว" },
          ],
        }),
        faqPageSchema([
          { q: "Police Clearance ประเภทไหนที่ฉันต้องใช้?", a: "ขึ้นอยู่กับประเทศปลายทาง: USA ใช้ FBI Background Check, UK ใช้ ACRO Police Certificate, Australia ใช้ AFP National Police Check, Canada ใช้ RCMP Criminal Record Check, สำหรับใช้ในไทย/อาเซียนใช้ PCSC จาก ตม. ไทย" },
          { q: "ใช้เวลานานเท่าใดในการขอ?", a: "PCSC ไทย 7-15 วันทำการ, FBI 4-8 สัปดาห์ (Express 1-2 สัปดาห์), ACRO 10 วันทำการ (Premium 2 วัน), AFP 15 วันทำการ, RCMP 30+ วันทำการ" },
          { q: "ราคาเริ่มต้นเท่าไหร่?", a: "บริการประสานงาน NYC เริ่ม 2,000 บาท ยังไม่รวมค่าธรรมเนียมตำรวจ 100 บาท (ระเบียบ สตช. ว่าด้วยค่าธรรมเนียม 2562) ค่ารับรอง MFA 400 บาท/ตรา (กฎกระทรวงการต่างประเทศ 2562) และค่า Apostille 800 บาท ตามอนุสัญญา Hague (มีผล 15 ธ.ค. 2025) — แพ็คเกจครบจบเริ่ม 3,500 บาท ใช้เวลา 7–14 วันทำการ" },
          { q: "ต้องเดินทางไปเองหรือไม่?", a: "ไม่ต้อง — NYC รับมอบอำนาจตาม ป.พ.พ. ม.797 ดำเนินการแทนได้ ยกเว้นการพิมพ์ลายนิ้วมือที่ต้องมาแสดงตนครั้งเดียวที่ ศพฐ. สำนักงานตำรวจแห่งชาติ (อาคาร 24 ถ.พระราม 1) หรือสถานีตำรวจในเขต หากอยู่ต่างประเทศใช้สถานทูตไทย/ตำรวจท้องถิ่นพิมพ์บนแบบ FD-258 แล้วส่งกลับได้" },
          { q: "ใบ Police Clearance หมดอายุเมื่อไหร่?", a: "อายุ 6 เดือนนับจากวันออก ตามระเบียบ สตช. 2562 ข้อ 8 สำหรับยื่นวีซ่า/PR/Work Permit — สถานทูตออสเตรเลีย (Subclass 189/190/491) และ Home Office UK รับใบอายุไม่เกิน 12 เดือน ส่วน USCIS (I-485) รับไม่เกิน 24 เดือน แนะนำใช้ภายใน 90 วันเพื่อให้ทันรอบพิจารณา" },
        ]),
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: "Police Clearance & Criminal Record Check Service",
              serviceType: "Police Clearance Coordination",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                telephone: "+66832494999",
                url: SITE,
              },
              offers: {
                "@type": "Offer",
                price: 2000,
                priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — Public MCP Server",
          description: "AI agent endpoint exposing 20+ typed tools for Police Clearance coordination (PCSC, FBI, ACRO, AFP, RCMP).",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: PoliceHubPage,
});

function PoliceHubPage() {
  const sections = policeHubSectionsTh();
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Police Clearance</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Police Clearance · Criminal Record Check
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            ตรวจประวัติอาชญากรรมทุกประเทศ — PCSC ไทย, FBI, ACRO, AFP, RCMP
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการขอตรวจประวัติอาชญากรรมครบวงจร — 8 ประเภทเอกสาร · 60+ ประเทศปลายทาง · แปล + MFA/Apostille
            ในแพ็กเกจเดียว เริ่ม ฿2,000 โดยทนายขึ้นทะเบียนสภาทนายความฯ
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Police Clearance Thailand">
              NYC Language Institute รับยื่นตรวจประวัติอาชญากรรม (Police Clearance / PCSC) ครบ 8 ประเภท — PCSC ไทย, FBI Identity History (US), ACRO (UK), AFP National Police Check (AU), RCMP (CA), German Führungszeugnis, French B3 และ Apostille/MFA legalization — ครอบคลุม 60+ ประเทศปลายทาง เริ่ม ฿2,000 ดำเนินการ 7-25 วันทำการ พร้อมแปลและรับรองในแพ็กเกจเดียว ผ่านรอบแรก 99.6% จาก
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "ราคาเริ่มต้น", v: "฿2,000 / ใบ" },
              { k: "ระยะเวลา", v: "7-25 วันทำการ" },
              { k: "ประเภทเอกสาร", v: "8 ประเทศต้นทาง" },
              { k: "ปลายทาง", v: "60+ ประเทศ" },
              { k: "ผ่านรอบแรก", v: "99.6%" },
              { k: "DBD", v: "0435567000061" },
            ]} />
          </div>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 -mt-6">
        <AIAnswerBox
          lang="th"
          id="ai-answer-police"
          question="ขอตรวจประวัติอาชญากรรม Police Clearance ราคาเท่าไรและใช้กี่วัน?"
          answer="NYC Language Institute ขอตรวจประวัติ PCSC ไทย, FBI, ACRO, AFP, RCMP และอีกหลายประเทศ เริ่ม ฿2,000 ใช้เวลา 7–25 วันทำการ พร้อมแปลและรับรอง MFA/Apostille ในแพ็กเกจเดียว ผ่านรอบแรก 99.6% จาก"
          stats={[
            { label: "ราคาเริ่ม", value: "฿2,000" },
            { label: "ระยะเวลา", value: "7–25 วัน" },
            { label: "ประเทศ", value: "60+" },
            { label: "ผ่านรอบแรก", value: "99.6%" },
          ]}
          sourceUrl="https://nyclanguageinstitute.com/police-clearance"
        />
      </section>



      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <PoliceSections sections={sections} />

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เลือกประเภทบริการ Police Clearance</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {POLICE_CLEARANCE_TYPES.map((t) => (
              <Link
                key={t.slug}
                to="/police-clearance/$type"
                params={{ type: t.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {t.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  เริ่ม ฿{t.priceFromTh.toLocaleString()} · {t.turnaround}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            สำหรับประเทศปลายทาง (60+ ประเทศ)
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {TARGET_COUNTRIES.map((c) => (
              <Link
                key={c.slug}
                to="/police-clearance/for/$country"
                params={{ country: c.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 transition hover:border-gold/50 hover:text-amber-700"
              >
                <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                <span className="font-semibold">{c.nameTh}</span>
                <span className="ml-auto text-[10px] text-slate-500">{c.hague ? "Apostille" : "Embassy"}</span>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-14">
          <ContactCTA />
        </div>
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="th" variant="light" />
          <HubCountryLinks service="police-clearance" lang="th" />
          <ServicePageEnhancer serviceName="ตรวจประวัติอาชญากรรม" locale="th" />
      <SiteCoreLinks />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
